import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash } from '@phosphor-icons/react';
import { InlineSkeleton } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useToast } from '@/hooks/use-toast';
import {
  MIN_CATEGORIES_WITH_PEERS,
  PEER_CATEGORIES,
  PeerCategoryKey,
} from '@/modules/shared/hr-form-engine';
import { EmployeeSearchPicker, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { useGetPeerRequestDetails, useSavePeerSelection } from '../../hooks/use-peer-requests';
import { PeerRequest, SelectedPeer } from '../../types/peer-request.types';

export interface SelectPeersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: PeerRequest | null;
  managerId: string;
}

const parseSkills = (input: string) => [
  ...new Set(
    input
      .split(',')
      .map((skill) => skill.trim())
      .filter(Boolean)
  ),
];

export const SelectPeersDialog = ({
  open,
  onOpenChange,
  request,
  managerId,
}: SelectPeersDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [peers, setPeers] = useState<SelectedPeer[]>([]);
  const [skillsInput, setSkillsInput] = useState('');
  const [category, setCategory] = useState<PeerCategoryKey>(PEER_CATEGORIES[0].key);
  const [addedNames, setAddedNames] = useState<Record<string, string>>({});

  const pmReviewId = open ? request?.review.ItemId : undefined;
  const { data: details, isLoading } = useGetPeerRequestDetails(pmReviewId);
  const { byUserId, isLoading: isNamesLoading } = useEmployeesByUserIds(
    (details ?? []).map((detail) => detail.PeerUserId)
  );
  const { mutateAsync: savePeers, isPending } = useSavePeerSelection();

  const lockedSkills = request?.peerData.PeerSkills ?? [];
  const hasLockedSkills = lockedSkills.length > 0;

  // Rebuild the working list from the stored peer requests every time the dialog opens or the data refreshes.
  useEffect(() => {
    if (!open || !details) return;
    setPeers(
      details.map((detail) => ({
        peerUserId: detail.PeerUserId,
        displayName: byUserId.get(detail.PeerUserId)?.DisplayName ?? detail.PeerUserId,
        category: (detail.Category || 'Other') as PeerCategoryKey,
        isExisting: true,
        isLocked: detail.ReportingManagerId !== managerId,
      }))
    );
  }, [open, details, byUserId, managerId]);

  useEffect(() => {
    if (open) setSkillsInput(lockedSkills.join(', '));
    // lockedSkills is derived from the request; resetting on open is intended
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request?.review.ItemId]);

  const categoriesCovered = useMemo(
    () => new Set(peers.map((peer) => peer.category)).size,
    [peers]
  );

  if (!request) return null;

  const revieweeId = request.review.ReviewForId;

  const addPeer = (employee: { UserId: string; DisplayName: string }) => {
    if (employee.UserId === revieweeId) {
      toast({
        title: t('CANNOT_ADD_SELF_AS_PEER', { name: employee.DisplayName }),
        variant: 'destructive',
      });
      return;
    }
    setAddedNames((names) => ({ ...names, [employee.UserId]: employee.DisplayName }));
    setPeers((current) => [
      ...current,
      {
        peerUserId: employee.UserId,
        displayName: employee.DisplayName,
        category,
        isExisting: false,
        isLocked: false,
      },
    ]);
  };

  const removePeer = (peerUserId: string) =>
    setPeers((current) => current.filter((peer) => peer.peerUserId !== peerUserId));

  const handleSave = async () => {
    try {
      await savePeers({
        request,
        managerId,
        peers: peers.map((peer) => ({ peerUserId: peer.peerUserId, category: peer.category })),
        skills: hasLockedSkills ? lockedSkills : parseSkills(skillsInput),
      });
      onOpenChange(false);
    } catch {
      // surfaced by the mutation's onError toast
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('SELECT_PEERS_FOR', { name: request.review.Name })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('SHARED_SKILLS')}</Label>
            <Input
              placeholder={t('SKILLS_PLACEHOLDER')}
              value={skillsInput}
              disabled={hasLockedSkills}
              onChange={(event) => setSkillsInput(event.target.value)}
            />
            {hasLockedSkills && (
              <p className="text-xs text-muted-foreground">{t('SKILLS_LOCKED_BY_FIRST_MANAGER')}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1 sm:order-first">
              <EmployeeSearchPicker
                excludeIds={[revieweeId, ...peers.map((peer) => peer.peerUserId)]}
                onSelect={addPeer}
              />
            </div>
            <div className="order-first w-full shrink-0 space-y-1.5 sm:order-none sm:w-36">
              <Label>{t('CATEGORY')}</Label>
              <Select
                value={category}
                onValueChange={(value) => setCategory(value as PeerCategoryKey)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PEER_CATEGORIES.map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {t(option.title)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {t('CATEGORIES_COVERED', {
                covered: categoriesCovered,
                required: MIN_CATEGORIES_WITH_PEERS,
              })}
            </p>
            {isLoading &&
              Array.from({ length: 3 }, (_, index) => (
                <Skeleton key={index} className="h-10 w-full rounded-md" />
              ))}
            {peers.map((peer) => (
              <div
                key={peer.peerUserId}
                className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {/* A saved peer's name is looked up: a placeholder, not their raw id. */}
                  {peer.isExisting && isNamesLoading ? (
                    <InlineSkeleton className="w-28" />
                  ) : (
                    (addedNames[peer.peerUserId] ?? peer.displayName)
                  )}{' '}
                  — {t(PEER_CATEGORIES.find((c) => c.key === peer.category)?.title ?? '')}
                </span>
                {!peer.isLocked && (
                  <Button variant="ghost" size="icon" onClick={() => removePeer(peer.peerUserId)}>
                    <Trash className="size-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <DialogCancelButton onClick={() => onOpenChange(false)}>{t('CANCEL')}</DialogCancelButton>
          <Button
            type="button"
            onClick={handleSave}
            loading={isPending}
            disabled={isPending || isLoading}
          >
            {t('SAVE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
